/**
 * studio-shop D5, D6, D8 — the shop's checkout (`/shop/checkout`): the buyer's
 * email and shipping address, the shipping line every service in the order has
 * confirmed for that address, then the payment, beside the order's summary. A
 * guest buys with an email; a signed-in buyer finds the email and address of
 * their last paid order here filled in, and can change them. The server sets
 * every amount. A money piece: a custom site may restyle it but never redraw it.
 */
import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'shop-checkout',
  description:
    'The shop checkout: email, ship to, the shipping every service confirmed for the address, and the payment, beside the order summary. A signed-in buyer finds their last address filled in.',
  drawing: 'standard-only',
  options: {},
  parts: {
    page: { description: 'The whole checkout.', required: false },
    title: { description: 'Checkout.', required: true },
    back: { description: 'Back to the bag.', required: false },
    telegram: { description: 'Sign in with Telegram to have the address filled in next time, or who is signed in.', required: false },
    signIn: { description: 'Sign in with Telegram.', required: false, role: 'secondary-action' },
    email: { description: "The email the receipt goes to; with a website form's answers in a guest's bag, the buyer's own name too, headed About you.", required: true },
    shipTo: { description: 'The name and address the order ships to.', required: true },
    field: { description: 'One field: its label, its input and what is missing.', required: true },
    savedAddress: { description: "A signed-in buyer's saved address, with Change.", required: true },
    change: { description: 'Change the saved address.', required: true },
    shipping: { description: 'The shipping line: free or its price with when it arrives, or why the address cannot be shipped to.', required: true },
    payment: { description: 'The payment: Continue to payment, then the card form.', required: true },
    adult: { description: "The required tick: I'm 18 or over, and this order ships in an adult's name.", required: true },
    continue: { description: 'Continue to payment, once the details are complete, shipping is known and the buyer is 18 or over.', required: true, role: 'primary-action' },
    expired: { description: 'The checkout expired: review the order and try again.', required: true },
    problem: { description: "Why the order can't be placed now, in the server's words.", required: true },
    summary: { description: 'The order summary beside the form.', required: true },
    summaryToggle: { description: 'On a phone: show or hide the summary, with the total.', required: false },
    item: { description: 'One item in the summary.', required: true },
    itemName: { description: "An item's product name in the summary.", required: false, role: 'title' },
    row: { description: 'Subtotal, shipping or sales tax in the summary.', required: true },
    total: { description: 'The total.', required: true },
  },
  actions: {
    setField: {
      description: "Sets one field: email, buyerName (the buyer's own name, asked only with a website form's answers in a guest's bag), name (the parcel's), country, line1, line2, city, state or postalCode.",
      args: [
        { name: 'field', kind: 'value', type: 'string', values: ['email', 'buyerName', 'name', 'country', 'line1', 'line2', 'city', 'state', 'postalCode'] },
        { name: 'value', kind: 'value', type: 'string', format: 'text', maxLength: 254 },
      ],
    },
    changeAddress: { description: 'Opens the saved address to change it.', args: [] },
    signIn: { description: 'Signs in with Telegram.', args: [] },
    setAdult: {
      description: "Ticks or clears the buyer's confirmation that they are 18 or over and the order ships in an adult's name.",
      args: [{ name: 'confirmed', kind: 'value', type: 'boolean' }],
    },
    continue: { description: 'Checks the details and, when they are complete, places the order and opens the payment.', args: [] },
    back: { description: 'Goes back to the bag.', args: [] },
  },
  events: {},
});

/** The checkout's fields. */
export type ShopCheckoutField = 'email' | 'buyerName' | 'name' | 'country' | 'line1' | 'line2' | 'city' | 'state' | 'postalCode';

/** One item in the summary. */
export interface ShopCheckoutItem {
  readonly optionId: string;
  readonly name: string;
  /** The option's label ("Paper 18 × 24 in"). */
  readonly option: string;
  readonly imageUrl: string | null;
  /** The size whose shape the picture is drawn in ("18 × 24 in"); null for the product's own picture, drawn whole. */
  readonly size: string | null;
  /** The picture is a canvas's own: draw only the canvas front (the kit's PrintPicture does), not the wrap around the stretcher. */
  readonly canvas: boolean;
  readonly quantity: number;
  /** The price for the quantity: "$190.00". */
  readonly total: string;
}

/** What the checkout knows. */
export interface ShopCheckoutState {
  /** loading while the bag is priced, empty when nothing in it can be bought, else ready. */
  readonly status: 'loading' | 'empty' | 'ready';
  /** Who is here: loading, signed out or signed in. */
  readonly session: 'loading' | 'signedOut' | 'signedIn';
  /** The signed-in buyer's name; null when signed out. */
  readonly client: string | null;
  /** True when the studio offers Sign in with Telegram. */
  readonly canSignIn: boolean;
  /**
   * site-form-buy-mode (walk row 8): a guest whose bag carries a website form's answers gives their own name too, apart
   * from the parcel's, since a gift may ship to someone else. False for every other checkout.
   */
  readonly asksBuyerName: boolean;
  /** The fields as filled in. */
  readonly form: Readonly<Record<Exclude<ShopCheckoutField, 'buyerName'>, string> & { buyerName?: string }>;
  /** What is missing or wrong, per field, once the buyer has tried to continue. */
  readonly errors: Readonly<Partial<Record<ShopCheckoutField, string>>>;
  /** Bumped on each attempt to continue with something missing: the drawing moves to the first. */
  readonly attempt: number;
  /** The countries the checkout ships to, by code and name. */
  readonly countries: readonly { readonly code: string; readonly name: string }[];
  /** The US states by code; the state is chosen from these for a US address. */
  readonly states: readonly string[];
  /** A signed-in buyer's saved address, shown until they change it; null otherwise. */
  readonly savedAddress: { readonly name: string; readonly line: string } | null;
  /** True when the email and address came from the buyer's last paid order. */
  readonly filledIn: boolean;
  /** The shipping: waiting for a complete address, being worked out, priced ("Free" or "$24.95"), or refused with the reason. */
  readonly shipping:
    | { readonly status: 'waiting' | 'working' }
    | {
        readonly status: 'priced';
        readonly price: string;
        /** D5: when it arrives ("Arrives Nov 2 to 6"); null when a service gives no estimate. */
        readonly arrives?: string | null;
        /** D5: true outside the US, where import taxes may be charged on arrival. */
        readonly importTaxes?: boolean;
      }
    | { readonly status: 'refused'; readonly reason: string };
  /** The summary's items, those still available. */
  readonly items: readonly ShopCheckoutItem[];
  readonly subtotal: string | null;
  /** The shipping row: "Free", "$24.95", or "From your address" before it is known. */
  readonly shippingRow: string;
  /** The sales tax row, only when the address is taxed; null otherwise. */
  readonly tax: string | null;
  /** The total once shipping is known; null before. */
  readonly total: string | null;
  /** details (filling in), placing (the order is being made), or payment (the card form for the order). */
  readonly step: 'details' | 'placing' | 'payment';
  /** The order being paid, once placed; null before. */
  readonly order: { readonly id: string; readonly claimToken: string | null; readonly total: string; readonly currency: string } | null;
  /** True once the buyer ticked that they are 18 or over and the order ships in an adult's name; never ticked for them. */
  readonly adultConfirmed: boolean;
  /** True once the order the buyer was paying for expired: they review and continue again. */
  readonly expired: boolean;
  /** Why the order can't be placed now, in the server's words; null otherwise. */
  readonly problem: string | null;
}

/** What a drawing can ask the checkout to do. */
export interface ShopCheckoutActions {
  readonly setField: (field: ShopCheckoutField, value: string) => void;
  readonly changeAddress: () => void;
  readonly signIn: () => void;
  /** Tick (true) or clear (false) the 18-or-over confirmation. */
  readonly setAdult: (confirmed: boolean) => void;
  readonly continue: () => void;
  readonly back: () => void;
}

/** The checkout reports nothing to its host. */
export type ShopCheckoutEvents = Record<string, never>;
